import {
  createEligibilityCheck,
  type EligibilityIntegrationProvider,
} from './eligibilityChecksService'
import { STEDI_API_DATE } from './stediService'

export const ELIGIBILITY_PERSISTENCE_META_KEY = '_eligibilityPersistence'

export type EligibilityPersistenceMeta = {
  integrationProvider: EligibilityIntegrationProvider
  stediApiDate?: string
  requestPayload: Record<string, unknown>
  responsePayload: Record<string, unknown>
}

/** Optional overrides when saving POST /eligibility-checks. */
export type EligibilityCheckPersistContext = {
  /** Backend `patient_id` — intake uses subscriber / member ID. */
  patientId?: string
  payorId?: string
  patientIntakeId?: string
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

function getStediEligibilityResponse(
  meta: EligibilityPersistenceMeta
): Record<string, unknown> | null {
  return asRecord(meta.responsePayload.response) ?? asRecord(meta.responsePayload)
}

function getStediSubscriber(meta: EligibilityPersistenceMeta): Record<string, unknown> | null {
  return asRecord(getStediEligibilityResponse(meta)?.subscriber)
}

/** Stedi `patient_id` = `response.subscriber.memberId`. */
export function resolveMemberIdForEligibilityCheck(
  formData: { subscriberID?: string; policyNumber?: string },
  parsed?: Record<string, unknown>
): string {
  const meta = parsed ? readEligibilityPersistenceMeta(parsed) : null
  if (meta?.integrationProvider === 'stedi') {
    const subscriber = getStediSubscriber(meta)
    const memberId = String(subscriber?.memberId ?? subscriber?.memberID ?? '')
      .replace(/\s+/g, '')
      .trim()
    if (memberId) return memberId
  }

  const fromForm = (formData.subscriberID || formData.policyNumber || '')
    .replace(/\s+/g, '')
    .trim()
  if (fromForm) return fromForm

  if (!meta) return ''

  const payload = asRecord(meta.requestPayload.payload)
  const subscriber = asRecord(payload?.subscriber)
  const memberId = String(subscriber?.memberId ?? subscriber?.memberID ?? '')
    .replace(/\s+/g, '')
    .trim()
  return memberId
}

/**
 * Stedi `patient_intake_id` = `response.subscriber.groupNumber` (then `planNumber`).
 */
export function resolvePatientIntakeIdForEligibilityCheck(
  parsed: Record<string, unknown>,
  formData?: { groupNumber?: string; policyNumber?: string }
): string {
  const meta = readEligibilityPersistenceMeta(parsed)
  if (!meta) return ''

  if (meta.integrationProvider === 'stedi') {
    const subscriber = getStediSubscriber(meta)
    const groupNumber = String(subscriber?.groupNumber ?? '').trim()
    if (groupNumber) return groupNumber
    const planNumber = String(subscriber?.planNumber ?? '').trim()
    if (planNumber) return planNumber
  }

  return String(formData?.groupNumber ?? formData?.policyNumber ?? '').trim()
}

/**
 * Stedi: `payor_id` = `response.payer.payorIdentification`.
 * pVerify fallback: `PverifyPayerCode` / form `payerCode`.
 */
export function resolvePayorIdForEligibilityCheck(
  parsed: Record<string, unknown>,
  formData?: { payerCode?: string }
): string {
  const meta = readEligibilityPersistenceMeta(parsed)
  if (!meta) return ''

  if (meta.integrationProvider === 'stedi') {
    const stediResponse = getStediEligibilityResponse(meta)
    const payer = asRecord(stediResponse?.payer)
    const payorIdentification = String(payer?.payorIdentification ?? '').trim()
    if (payorIdentification) return payorIdentification

    const requestPayload = asRecord(meta.requestPayload.payload)
    const tradingPartner = String(requestPayload?.tradingPartnerServiceId ?? '').trim()
    if (tradingPartner) return tradingPartner
  }

  if (meta.integrationProvider === 'pverify') {
    const response = asRecord(meta.responsePayload)
    const fromResponse = String(
      response?.PverifyPayerCode ?? response?.pverifyPayerCode ?? response?.PayerCode ?? ''
    ).trim()
    if (fromResponse) return fromResponse
  }

  return String(formData?.payerCode ?? '').trim()
}

export type EligibilityCheckPersistResult = {
  eligibilityCheckId?: string
}

export function readEligibilityPersistenceMeta(
  parsed: Record<string, unknown>
): EligibilityPersistenceMeta | null {
  const meta = parsed[ELIGIBILITY_PERSISTENCE_META_KEY]
  if (!meta || typeof meta !== 'object') return null
  const record = meta as Record<string, unknown>
  const integrationProvider = record.integrationProvider
  if (integrationProvider !== 'stedi' && integrationProvider !== 'pverify') return null
  const requestPayload = record.requestPayload
  const responsePayload = record.responsePayload
  if (!requestPayload || typeof requestPayload !== 'object') return null
  if (!responsePayload || typeof responsePayload !== 'object') return null
  return {
    integrationProvider,
    stediApiDate: typeof record.stediApiDate === 'string' ? record.stediApiDate : undefined,
    requestPayload: requestPayload as Record<string, unknown>,
    responsePayload: responsePayload as Record<string, unknown>,
  }
}

/** Removes internal persistence metadata before eligibility view mapping. */
export function stripEligibilityPersistenceMeta(
  parsed: Record<string, unknown>
): Record<string, unknown> {
  const { [ELIGIBILITY_PERSISTENCE_META_KEY]: _removed, ...rest } = parsed
  return rest
}

/** Saves Stedi / pVerify request + response to POST /eligibility-checks only. */
export async function saveEligibilityCheckFromResponse(
  parsed: Record<string, unknown>,
  context: EligibilityCheckPersistContext = {},
  formData?: {
    payerCode?: string
    subscriberID?: string
    policyNumber?: string
    groupNumber?: string
  }
): Promise<EligibilityCheckPersistResult> {
  const meta = readEligibilityPersistenceMeta(parsed)
  if (!meta) {
    throw new Error('Eligibility response is missing persistence metadata.')
  }

  if (meta.integrationProvider === 'stedi') {
    const upstream = asRecord(meta.responsePayload) ?? {}
    const response = asRecord(upstream.response)
    const apiDate = String(upstream.api_date ?? meta.stediApiDate ?? STEDI_API_DATE).trim()
    const upstreamStatusRaw = upstream.upstream_status
    const upstreamStatus =
      typeof upstreamStatusRaw === 'number'
        ? upstreamStatusRaw
        : Number.parseInt(String(upstreamStatusRaw ?? ''), 10)
    const upstreamUrl = String(upstream.upstream_url ?? '').trim()

    if (!response) {
      throw new Error('Stedi save failed: missing response object from eligibility API payload.')
    }
    if (!Number.isFinite(upstreamStatus)) {
      throw new Error('Stedi save failed: missing upstream_status in eligibility API payload.')
    }
    if (!upstreamUrl) {
      throw new Error('Stedi save failed: missing upstream_url in eligibility API payload.')
    }

    const saved = await createEligibilityCheck({
      integration_provider: 'stedi',
      request_payload: meta.requestPayload,
      api_date: apiDate,
      upstream_status: upstreamStatus,
      upstream_url: upstreamUrl,
      response,
    })

    return {
      eligibilityCheckId: typeof saved.id === 'string' ? saved.id : undefined,
    }
  }

  const patientId =
    context.patientId?.trim() ||
    resolveMemberIdForEligibilityCheck(
      {
        subscriberID: formData?.subscriberID,
        policyNumber: formData?.policyNumber,
      },
      parsed
    )
  if (!patientId) {
    throw new Error(
      'Member ID is required to save eligibility (Stedi response.subscriber.memberId or subscriber ID on the form).'
    )
  }
  const payorId = context.payorId?.trim() || resolvePayorIdForEligibilityCheck(parsed, formData)
  if (!payorId) {
    throw new Error(
      meta.integrationProvider === 'stedi'
        ? 'Payor ID is required to save eligibility (Stedi payer.payorIdentification was not found in the response).'
        : 'Payor ID is required to save eligibility (payer code was not found).'
    )
  }
  const patientIntakeId =
    context.patientIntakeId?.trim() ||
    resolvePatientIntakeIdForEligibilityCheck(parsed, formData)
  if (!patientIntakeId) {
    throw new Error(
      meta.integrationProvider === 'stedi'
        ? 'Patient intake ID is required (Stedi response.subscriber.groupNumber or planNumber was not found).'
        : 'Patient intake ID is required (group number was not found).'
    )
  }

  const saved = await createEligibilityCheck({
    patient_id: patientId,
    payor_id: payorId,
    patient_intake_id: patientIntakeId,
    integration_provider: meta.integrationProvider,
    status: 'complete',
    ...(meta.integrationProvider === 'stedi'
      ? { stedi_api_date: meta.stediApiDate ?? STEDI_API_DATE }
      : {}),
    request_payload: meta.requestPayload,
    response_payload: meta.responsePayload,
  })

  return {
    eligibilityCheckId: typeof saved.id === 'string' ? saved.id : undefined,
  }
}
